'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { FolderKanban, ArrowLeft, ChevronDown, ChevronUp, Calendar, Users, MessageSquare, FileText, CheckCircle2, Clock,  } from 'lucide-react';
import Link from 'next/link';
import Badge from '@/components/ui/Badge';

interface Milestone {
  label: string;
  date: string;
  done: boolean;
}

interface ClientProject {
  id: string;
  name: string;
  serviceType: string;
  stage: string;
  stageVariant: string;
  completion: number;
  pm: string;
  startDate: string;
  deadline: string;
  budget: string;
  priority: string;
  priorityVariant: string;
  description: string;
  milestones: Milestone[];
  lastUpdate: string;
  updateNote: string;
}

const clientProjects: ClientProject[] = [
  {
    id: 'proj-001',
    name: 'Nexus ERP Platform',
    serviceType: 'ERP / CRM',
    stage: 'Testing',
    stageVariant: 'stage-testing',
    completion: 78,
    pm: 'Kavya Sharma',
    startDate: 'Mar 10, 2026',
    deadline: 'Sep 15, 2026',
    budget: '$84,000',
    priority: 'Critical',
    priorityVariant: 'priority-critical',
    description: 'Full-scale ERP platform covering inventory, HR, finance, and procurement modules with custom reporting dashboards.',
    milestones: [
      { label: 'Requirements Gathering', date: 'Mar 20, 2026', done: true },
      { label: 'UI/UX Design', date: 'Apr 15, 2026', done: true },
      { label: 'Core Module Development', date: 'Jun 30, 2026', done: true },
      { label: 'Integration & API', date: 'Jul 25, 2026', done: true },
      { label: 'QA & Testing', date: 'Aug 20, 2026', done: false },
      { label: 'Client Review', date: 'Sep 1, 2026', done: false },
      { label: 'Deployment', date: 'Sep 15, 2026', done: false },
    ],
    lastUpdate: '2 days ago',
    updateNote: 'QA team has completed 3 of 5 test suites. Performance testing in progress.',
  },
  {
    id: 'proj-003',
    name: 'BlueStar E-Commerce Portal',
    serviceType: 'Web Development',
    stage: 'Development',
    stageVariant: 'stage-development',
    completion: 54,
    pm: 'Anika Patel',
    startDate: 'May 5, 2026',
    deadline: 'Oct 1, 2026',
    budget: '$38,500',
    priority: 'High',
    priorityVariant: 'priority-high',
    description: 'Multi-vendor e-commerce platform with product catalog, cart, payment gateway integration, and admin panel.',
    milestones: [
      { label: 'Discovery & Planning', date: 'May 15, 2026', done: true },
      { label: 'Design Mockups', date: 'Jun 1, 2026', done: true },
      { label: 'Frontend Development', date: 'Jul 15, 2026', done: true },
      { label: 'Backend & Database', date: 'Aug 15, 2026', done: false },
      { label: 'Payment Integration', date: 'Sep 1, 2026', done: false },
      { label: 'Testing & Launch', date: 'Oct 1, 2026', done: false },
    ],
    lastUpdate: '5 days ago',
    updateNote: 'Frontend is complete. Backend API development is 60% done. Payment gateway integration starts next week.',
  },
  {
    id: 'proj-005',
    name: 'Meridian Cloud Migration',
    serviceType: 'Cloud / Hosting',
    stage: 'Development',
    stageVariant: 'stage-development',
    completion: 41,
    pm: 'Kavya Sharma',
    startDate: 'Jun 20, 2026',
    deadline: 'Sep 30, 2026',
    budget: '$29,400',
    priority: 'Medium',
    priorityVariant: 'priority-medium',
    description: 'Migration of on-premise infrastructure to AWS cloud with auto-scaling, load balancing, and CI/CD pipeline setup.',
    milestones: [
      { label: 'Infrastructure Audit', date: 'Jun 28, 2026', done: true },
      { label: 'Architecture Design', date: 'Jul 10, 2026', done: true },
      { label: 'Dev Environment Setup', date: 'Jul 25, 2026', done: false },
      { label: 'Data Migration', date: 'Aug 20, 2026', done: false },
      { label: 'Testing & Validation', date: 'Sep 15, 2026', done: false },
      { label: 'Go Live', date: 'Sep 30, 2026', done: false },
    ],
    lastUpdate: '1 week ago',
    updateNote: 'Architecture design approved. Dev environment provisioning in progress on AWS.',
  },
];

export default function ClientProjectsPage() {
  const [expandedId, setExpandedId] = useState<string | null>('proj-001');

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link href="/client-portal">
            <button className="p-2 rounded-lg border border-border hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground">
              <ArrowLeft size={16} />
            </button>
          </Link>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Client Portal</p>
            <h1 className="text-xl font-bold text-foreground">My Projects</h1>
          </div>
        </div>

        {/* Summary Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Active', count: 3, color: 'text-primary', bg: 'bg-primary/10' },
            { label: 'In Testing', count: 1, color: 'text-warning', bg: 'bg-warning/10' },
            { label: 'Completed', count: 7, color: 'text-success', bg: 'bg-success/10' },
          ].map((s) => (
            <div key={s.label} className="card-surface p-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                <FolderKanban size={17} className={s.color} />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{s.count}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Project Cards */}
        <div className="space-y-4">
          {clientProjects.map((proj) => {
            const isExpanded = expandedId === proj.id;
            const completedMilestones = proj.milestones.filter((m) => m.done).length;

            return (
              <div key={proj.id} className="card-surface overflow-hidden">
                {/* Card Header */}
                <button
                  onClick={() => toggleExpand(proj.id)}
                  className="w-full p-5 flex items-center gap-4 hover:bg-muted/20 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-foreground">{proj.name}</h3>
                      <Badge label={proj.stage} variant={proj.stageVariant as any} />
                      <Badge label={proj.priority} variant={proj.priorityVariant as any} />
                    </div>
                    <div className="flex items-center gap-4 text-2xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Users size={11} /> PM: {proj.pm}</span>
                      <span className="flex items-center gap-1"><Calendar size={11} /> Due: {proj.deadline}</span>
                      <span className="flex items-center gap-1"><Clock size={11} /> Updated {proj.lastUpdate}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-bold text-foreground">{proj.completion}%</p>
                      <p className="text-2xs text-muted-foreground">Complete</p>
                    </div>
                    <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden hidden sm:block">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${proj.completion}%` }} />
                    </div>
                    {isExpanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-border px-5 pb-5 pt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Milestones */}
                      <div className="lg:col-span-2">
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                          Project Milestones ({completedMilestones}/{proj.milestones.length})
                        </h4>
                        <div className="relative">
                          <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
                          <div className="space-y-3">
                            {proj.milestones.map((milestone, idx) => (
                              <div key={idx} className="flex items-center gap-3 pl-8 relative">
                                <div className={`absolute left-0 w-6 h-6 rounded-full flex items-center justify-center border-2 ${milestone.done ? 'bg-success/10 border-success' : 'bg-muted border-border'}`}>
                                  {milestone.done
                                    ? <CheckCircle2 size={12} className="text-success" />
                                    : <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                                  }
                                </div>
                                <div className="flex-1 flex items-center justify-between">
                                  <span className={`text-sm ${milestone.done ? 'text-foreground' : 'text-muted-foreground'}`}>
                                    {milestone.label}
                                  </span>
                                  <span className="text-2xs text-muted-foreground">{milestone.date}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-4">
                        <div className="bg-muted/30 rounded-lg p-4">
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Project Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Service</span>
                              <span className="text-foreground font-medium">{proj.serviceType}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Budget</span>
                              <span className="text-foreground font-medium">{proj.budget}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Start Date</span>
                              <span className="text-foreground font-medium">{proj.startDate}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Deadline</span>
                              <span className="text-foreground font-medium">{proj.deadline}</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-muted/30 rounded-lg p-4">
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Latest Update</h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">{proj.updateNote}</p>
                          <p className="text-2xs text-muted-foreground/60 mt-2">{proj.lastUpdate}</p>
                        </div>

                        <div className="flex gap-2">
                          <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors">
                            <MessageSquare size={13} /> Message PM
                          </button>
                          <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors">
                            <FileText size={13} /> Documents
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground leading-relaxed">{proj.description}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
